/** Step navigation, locking, derived step states, selection and focus management. */
import { nextTick } from 'vue';
import { changeLabel as domainChangeLabel, criticalBlocker, FIELD_LABELS, pendingCritical as domainPending } from '@/domain';
import { exportBlocker as packExportBlocker } from '@/export';
import { announce } from './announce';
import { app, isStep, shownBuild, snap, STEPS, ui, viewerRef, type SheetMode, type StepId } from './state';
import { plural } from './format';

// ---------------------------------------------------------------------------------------------
// Derived status
// ---------------------------------------------------------------------------------------------
export const LABELS: Record<string, string> = { ...FIELD_LABELS, openingRatios: 'Opening proportions', openings: 'Opening heights', family: 'Style' };
export const labelOf = (key: string): string => LABELS[key] || 'Value';
const NEEDS = ['proposed', 'published', 'unresolved'];

export function visibleFieldKeys(): string[] {
  const f = snap.value.fields;
  const order = ['family', 'repairs', 'W', 'H', 'D', 'bays', 'shelfCount', 'even', f.openings ? 'openings' : 'openingRatios', 'back', 'plinth', 'stockThickness', 'material', 'finish'];
  return order.filter((k) => f[k]);
}
export const fieldsToReview = (): string[] => visibleFieldKeys().filter((k) => NEEDS.includes(snap.value.fields[k]!.status));
export const optionalToReview = (): string[] => fieldsToReview().filter((k) => !snap.value.fields[k]!.critical && snap.value.fields[k]!.status !== 'unresolved');
export const pendingCritical = (): string[] => domainPending(snap.value.fields);
export const nextRevId = (): string => snap.value.nextRevisionId;
export const saveTarget = (): string => (snap.value.isDirty || !snap.value.currentRevision ? nextRevId() : snap.value.currentRevisionId!);
export const changeLabel = (fallback = 'Customised'): string => domainChangeLabel(app.store.currentRevision?.design, app.store.design, fallback);

/** "Exporting saves your edits as r5 first." | "Exporting uses r4 as it is." */
export function helper(verb: string): string {
  const s = snap.value;
  return s.isDirty || !s.currentRevision ? `${verb} saves your edits as ${nextRevId()} first.` : `${verb} uses ${s.currentRevisionId} as it is.`;
}

export function lockReason(stepId: StepId): string | null {
  if (stepId === 'reference') return null;
  if (ui.empty) return 'Add a reference and run the analysis first.';
  if (stepId === 'check') return null;
  const pend = pendingCritical().filter((k) => snap.value.fields[k]!.status !== 'unresolved');
  if (pend.length) return `Confirm ${pend.map((k) => (FIELD_LABELS[k] ?? k).toLowerCase()).join(', ')} in Check first.`;
  return null;
}

/** Exports and print need a valid design with every critical value confirmed (unresolved included). */
export function exportBlocker(): string | null {
  return packExportBlocker(snap.value.build, snap.value.fields) ?? (criticalBlocker(snap.value.fields) || null);
}

export interface StepState { kind: 'done' | 'attn' | 'busy' | 'todo' | 'locked'; text: string; reason?: string; progress?: number }
export function stepState(id: StepId): StepState {
  const lock = lockReason(id);
  if (lock) return { kind: 'locked', text: 'Locked', reason: lock };
  const b = snap.value.build;
  switch (id) {
    case 'reference': {
      const a = ui.analysis;
      if (a.status === 'running') return { kind: 'busy', text: 'Analysing', progress: ((a.stageIndex || 0) + 0.5) / 4 };
      if (a.status === 'refused' || a.status === 'error') return { kind: 'attn', text: 'Needs you' };
      if (ui.empty) return { kind: 'attn', text: 'Add a reference' };
      return { kind: 'done', text: a.status === 'manual' ? 'Set up by hand' : 'Analysed' };
    }
    case 'check': {
      const crit = pendingCritical().filter((k) => snap.value.fields[k]!.status !== 'unresolved').length;
      const unsure = fieldsToReview().filter((k) => snap.value.fields[k]!.status === 'unresolved').length;
      if (crit) return { kind: 'attn', text: `${crit} to confirm` };
      if (unsure) return { kind: 'attn', text: `${unsure} unsure` };
      const opt = optionalToReview().length;
      return opt ? { kind: 'todo', text: `${opt} optional` } : { kind: 'done', text: 'All checked' };
    }
    case 'shape':
      return b.validation.ok ? { kind: 'done', text: 'Valid' } : { kind: 'attn', text: plural(b.validation.errors.length, 'problem') };
    case 'look':
      return ui.looked ? { kind: 'done', text: 'Looked' } : { kind: 'todo', text: 'Optional' };
    case 'pack': {
      if (exportBlocker()) return { kind: 'attn', text: b.validation.ok ? 'Blocked' : 'Invalid' };
      const last = snap.value.exports[0];
      if (last && !snap.value.isDirty && last.revisionId === snap.value.currentRevisionId && last.manufacturingHash === b.manufacturingHash) return { kind: 'done', text: `Exported ${last.revisionId}` };
      return { kind: 'todo', text: 'Draft' };
    }
  }
  return { kind: 'todo', text: '' };
}

// ---------------------------------------------------------------------------------------------
// Focus
// ---------------------------------------------------------------------------------------------
let pendingFocus: string | null = null;
/** Focus `selector` after the next render (never drop focus to body). */
export function focusSoon(selector: string, { preventScroll }: { preventScroll?: boolean } = {}): void {
  pendingFocus = selector;
  const attempt = (last: boolean): boolean => {
    if (pendingFocus !== selector) return true;
    const el = document.querySelector<HTMLElement>(selector);
    if (!el) { if (last) pendingFocus = null; return false; }
    pendingFocus = null;
    const ps = preventScroll ?? selector === '#panelTitle';
    el.focus({ preventScroll: ps });
    if (!ps) requestAnimationFrame(() => el.scrollIntoView({ block: 'nearest' }));
    return true;
  };
  // after Vue's flush the target exists; one frame later as a fallback (e.g. a dialog opening)
  void nextTick(() => { if (!attempt(false)) requestAnimationFrame(() => attempt(true)); });
}
export const hasPendingFocus = (): boolean => pendingFocus !== null;

// ---------------------------------------------------------------------------------------------
// Sheet (≤ 1000 px)
// ---------------------------------------------------------------------------------------------
export const isCompact = (): boolean =>
  matchMedia('(max-width: 1000px)').matches && !matchMedia('(max-height: 500px) and (orientation: landscape)').matches;

let resizeT: ReturnType<typeof setTimeout> | null = null;
export function setSheet(mode: SheetMode): void {
  ui.sheet = mode;
  document.getElementById('studio')?.style.removeProperty('--stage-h');
  if (resizeT) clearTimeout(resizeT);
  resizeT = setTimeout(() => viewerRef.value?.resize(), 520);
}

// ---------------------------------------------------------------------------------------------
// Navigation
// ---------------------------------------------------------------------------------------------
let stepInT: ReturnType<typeof setTimeout> | null = null;
export function go(id: string, { replace = false, focus = true }: { replace?: boolean; focus?: boolean } = {}): void {
  let target: StepId = isStep(id) ? id : 'reference';
  const lock = lockReason(target);
  if (lock) { announce(lock); target = ui.empty ? 'reference' : 'check'; }
  const changed = target !== ui.step;
  if (changed && ui.step === 'check' && ui.analysis.status === 'review' && !pendingCritical().filter((k) => snap.value.fields[k]!.status !== 'unresolved').length) acceptProposalRevision();
  if (changed) {
    ui.sel = null;
    viewerRef.value?.select(null);
    if (ui.step === 'look') { ui.inspect = false; ui.explode = false; ui.frontsOpen = false; viewerRef.value?.setOptions({ exploded: false, detail: false, frontsOpen: false }); }
    if (target === 'look') ui.looked = true;
    if (isCompact()) setSheet(target === 'pack' ? 'full' : 'half');
    ui.checkEditing = null;
  }
  ui.step = target;
  const url = `#${target}`;
  if (location.hash !== url) history[replace ? 'replaceState' : 'pushState'](null, '', `${location.pathname}${location.search}${url}`);
  if (changed) {
    ui.stepIn = false;
    void nextTick(() => {
      ui.stepIn = true;
      if (stepInT) clearTimeout(stepInT);
      stepInT = setTimeout(() => { ui.stepIn = false; }, 800);
    });
    const n = STEPS.findIndex((s) => s.id === target) + 1;
    announce(`Step ${n} of ${STEPS.length}: ${STEPS[n - 1]!.label}.`);
    if (focus) focusSoon('#panelTitle');
  }
}

export function acceptProposalRevision(): void {
  const label = ui.analysis.proposal ? 'Proposal accepted' : 'Set up by hand';
  ui.analysis = { ...ui.analysis, status: ui.analysis.status === 'review' ? 'done' : ui.analysis.status };
  if (!snap.value.isDirty && snap.value.currentRevision) return;
  const rev = app.store.ensureRevision(label, { kind: label === 'Set up by hand' ? 'manual' : 'proposal' });
  announce(`Saved as ${rev.id}.`);
}

// ---------------------------------------------------------------------------------------------
// Selection
// ---------------------------------------------------------------------------------------------
export function select(id: string | null, { from = 'ui' }: { from?: 'ui' | 'viewer' } = {}): void {
  const b = shownBuild.value;
  ui.sel = id && b.parts.some((p) => p.id === id) ? id : null;
  if (from !== 'viewer') viewerRef.value?.select(ui.sel);
  if (ui.sel) announce(`${b.parts.find((p) => p.id === ui.sel)!.label} selected.`);
  if (ui.sel && (ui.step === 'pack' || ui.step === 'look')) {
    void nextTick(() => document.querySelector(`[data-part-row="${ui.sel}"]`)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }));
  }
}
export function hover(id: string | null, { from = 'ui' }: { from?: 'ui' | 'viewer' } = {}): void {
  ui.hov = id;
  if (from !== 'viewer') viewerRef.value?.hover(id);
}
/** Pick a front opening in Shape's selection mode; otherwise focus its height editor. */
export function pickOpening(i: number, additive = false): void {
  if (snap.value.viewing || ui.inspect || ui.explode) return;
  const o = snap.value.build.openings[i];
  if (!o) return;
  if (ui.step === 'shape' && ui.frontPicking) {
    const selected = ui.frontSelection.filter((id) => snap.value.build.openings.some((opening) => opening.id === id));
    ui.frontSelection = additive
      ? selected.includes(o.id) ? selected.filter((id) => id !== o.id) : [...selected, o.id]
      : [o.id];
    viewerRef.value?.highlightOpening(i);
    void nextTick(() => {
      const el = document.querySelector(`[data-fronts] [data-opening="${o.id}"] input`) as HTMLInputElement | null;
      el?.focus({ preventScroll: true });
      el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });
    announce(`${ui.frontSelection.length} opening${ui.frontSelection.length === 1 ? '' : 's'} picked for fronts.`);
    return;
  }
  ui.bay = o.bay ?? 0;
  ui.openingSel = o.index;
  viewerRef.value?.highlightOpening(i);
  if (ui.step !== 'shape' && !lockReason('shape')) go('shape', { focus: false });
  setTimeout(() => {
    const el = document.getElementById(`op-${o.index}`) as HTMLInputElement | null;
    if (el) { el.focus({ preventScroll: true }); el.select(); el.scrollIntoView({ block: 'center', behavior: 'smooth' }); }
  }, 60);
}

/** View a revision on the stage without changing anything; the current clean revision ends a view. */
export function viewRevision(id: string): void {
  ui.inspect = false;
  ui.sel = null;
  if (id === app.store.currentRevisionId && !app.store.isDirty) { app.store.backToLatest(); return; }
  if (app.store.view(id)) announce(`Viewing ${id} on the model. Nothing has changed. Choose Back to latest to return.`);
}
